import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const NeoFeedbackPage = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    positive: 0,
    negative: 0,
    neutral: 0,
    satisfactionRate: 0
  });

  useEffect(() => {
    fetchFeedbackData();
  }, []);

  const fetchFeedbackData = async () => {
    try {
      setLoading(true);
      
      // Fetch all conversations with feedback
      const response = await axios.get(`${API}/super-admin/neo/analytics/queries-log?page=1&limit=1000`);
      const allConversations = response.data.queries || [];
      
      // Calculate stats
      const withFeedback = allConversations.filter(c => c.feedback);
      const positive = withFeedback.filter(c => c.feedback === 'positive').length;
      const negative = withFeedback.filter(c => c.feedback === 'negative').length;
      const neutral = allConversations.length - withFeedback.length;
      const satisfactionRate = withFeedback.length > 0 ? (positive / withFeedback.length * 100).toFixed(1) : 0;
      
      setStats({
        total: allConversations.length,
        positive,
        negative,
        neutral,
        satisfactionRate
      });
      
      setConversations(allConversations);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch feedback data:', error);
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', { 
      day: '2-digit', 
      month: 'short',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const pieData = [
    { name: 'Positive', value: stats.positive, color: '#10b981' },
    { name: 'Negative', value: stats.negative, color: '#ef4444' },
    { name: 'No Feedback', value: stats.neutral, color: '#9ca3af' }
  ];

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600 text-lg">Loading feedback data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-8">
      {/* Header - Matching Dashboard Theme */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-bold">Neo User Feedback</h1>
            <p className="text-purple-200 text-sm">Monitor user satisfaction and feedback across all Neo conversations</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-200 hover:border-green-300">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-green-50 border border-gray-200">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-green-800">
                  {stats.positive}
                </div>
              </div>
            </div>
            <h3 className="text-gray-700 font-semibold text-base">Positive Feedback</h3>
            <p className="text-xs text-gray-500 mt-1">Thumbs up responses</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-200 hover:border-red-300">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-red-50 border border-gray-200">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                </svg>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-red-800">
                  {stats.negative}
                </div>
              </div>
            </div>
            <h3 className="text-gray-700 font-semibold text-base">Negative Feedback</h3>
            <p className="text-xs text-gray-500 mt-1">Thumbs down responses</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-200 hover:border-purple-300">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-purple-50 border border-gray-200">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-purple-800">
                  {stats.satisfactionRate}%
                </div>
              </div>
            </div>
            <h3 className="text-gray-700 font-semibold text-base">Satisfaction Rate</h3>
            <p className="text-xs text-gray-500 mt-1">Overall user satisfaction</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-200 hover:border-gray-300">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-gray-800">
                  {stats.neutral}
                </div>
              </div>
            </div>
            <h3 className="text-gray-700 font-semibold text-base">No Feedback</h3>
            <p className="text-xs text-gray-500 mt-1">Conversations without rating</p>
          </div>
        </div>
      </div>

      {/* Feedback Distribution Chart */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="bg-gradient-to-r from-indigo-100 to-purple-100 px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Feedback Distribution</h2>
          <p className="text-sm text-gray-600 mt-0.5">User satisfaction breakdown</p>
        </div>
        <div className="p-6">
          {stats.positive + stats.negative + stats.neutral > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">No feedback data available</div>
          )}
        </div>
      </div>

      {/* Recent Feedback with Comments */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="bg-gradient-to-r from-indigo-100 to-purple-100 px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Recent Feedback</h2>
          <p className="text-sm text-gray-600 mt-0.5">Latest user feedback and comments</p>
        </div>
        <div className="p-6">
          {conversations.filter(c => c.feedback).length > 0 ? (
            <div className="space-y-3">
              {conversations
                .filter(c => c.feedback)
                .slice(0, 20)
                .map((conv) => (
                  <div key={conv.conversation_id} className={`border-2 rounded-lg p-4 ${
                    conv.feedback === 'positive' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-bold text-gray-900">{conv.username}</span>
                        <span className={`px-3 py-0.5 text-xs font-bold rounded-full ${
                          conv.role === 'admin' 
                            ? 'bg-purple-100 text-purple-800' 
                            : conv.role === 'team_member'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {conv.role}
                        </span>
                        <span className="text-xs text-gray-500 font-medium">
                          {formatTimestamp(conv.timestamp)}
                        </span>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                        conv.feedback === 'positive' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-red-600 text-white'
                      }`}>
                        {conv.feedback === 'positive' ? '👍 Positive' : '👎 Negative'}
                      </div>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded-lg p-3 mb-2">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Query:</p>
                      <p className="text-sm text-gray-900">{conv.user_message}</p>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                      <p className="text-xs font-semibold text-blue-700 mb-1">Response:</p>
                      <p className="text-sm text-gray-700">
                        {conv.ai_response.substring(0, 200)}
                        {conv.ai_response.length > 200 ? '...' : ''}
                      </p>
                    </div>
                    
                    {conv.feedback_comment && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-yellow-800 mb-1">User Comment:</p>
                        <p className="text-sm text-gray-800 italic">"{conv.feedback_comment}"</p>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
              </svg>
              <p className="text-gray-500 font-medium">No feedback received yet</p>
              <p className="text-gray-400 text-sm mt-1">User feedback will appear here once users rate Neo responses</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NeoFeedbackPage;
